import { doc, setDoc, updateDoc, serverTimestamp, collection, getDocs, query, where, getDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../sdk_initialization';
// import { writeReservationLog } from './reservationLogsService';

const COLLECTION = 'cartItems';

const makeUuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Create a cart item document with a stable id (cartItemId) so we can update it later.
 * The document ID will be equal to cartItemId.
 */
export const createCartItem = async ({
  cartItemId,
  eventId,
  layoutId,
  profileId,
  sessionId,
  booth,
  price,
  reservationId, // optional: id of boothReservations doc for this booth
}) => {
  if (!db) throw new Error('Database not initialized');
  if (!eventId || !layoutId || !profileId || !sessionId || !booth?.id) {
    throw new Error('Missing required fields for createCartItem');
  }
  const id = cartItemId || makeUuid();
  const ref = doc(db, COLLECTION, id);
  // Price is already in cents, use as-is
  const priceInCents = (typeof price === 'number' && !isNaN(price)) ? price : null;
  const payload = {
    cartItemId: id,
    eventId,
    layoutId,
    profileId,
    sessionId,
    boothId: booth.id,
    boothNumber: (typeof booth.number === 'number' || typeof booth.number === 'string') ? String(booth.number) : null,
    level: booth.level ?? null,
    corner: booth.corner ?? null,
    price: priceInCents,
    reservationId: reservationId || `${eventId}_${layoutId}_${booth.id}`,
    status: 'added',
    addedAt: serverTimestamp(),
    removedAt: null,
    paidAt: null,
    lastAction: 'added',
    lastActionAt: serverTimestamp(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  await setDoc(ref, payload);
  // Compute remaining time (seconds) until cart expiry using reservation expiresAt if available.
  let remainingTimeSeconds = null;
  try {
    const resRef = doc(db, 'boothReservations', reservationId || `${eventId}_${layoutId}_${booth.id}`);
    const resSnap = await getDoc(resRef);
    if (resSnap.exists()) {
      const rData = resSnap.data() || {};
      const toMillis = (ts) => {
        try {
          if (!ts) return null;
          if (typeof ts.toMillis === 'function') return ts.toMillis();
          if (ts.seconds) return ts.seconds * 1000;
          const d = new Date(ts);
          return isNaN(d.getTime()) ? null : d.getTime();
        } catch { return null; }
      };
      const expiresMs = toMillis(rData.expiresAt);
      if (typeof expiresMs === 'number') {
        remainingTimeSeconds = Math.max(0, Math.floor((expiresMs - Date.now()) / 1000));
      }
    }
  } catch {}
  // Client-side reservation log writes are disabled.
  // Logging for setpending/add/remove/cancel paths is handled in backend functions.
  return id;
};

/**
 * Update an existing cart item with a new action (removed | paid | checkedout | expired).
 */
export const updateCartItemAction = async (cartItemId, action, options = {}) => {
  if (!db) throw new Error('Database not initialized');
  if (!cartItemId || !['removed', 'paid', 'checkedout', 'expired'].includes(action)) {
    throw new Error('Invalid parameters for updateCartItemAction');
  }
  const { suppressLog = false, triggeredByProfileIdOverride = null, triggeredByOverride = null } = options;
  const ref = doc(db, COLLECTION, cartItemId);
  // Snapshot before update for logging context
  let before = null;
  try { 
    const snap = await getDoc(ref); 
    if (snap.exists()) {
      before = snap.data();
    } else {
      return; // Can't update a non-existent item
    }
  } catch (err) {
    console.error('[updateCartItemAction] Error fetching cart item', cartItemId, err);
    return;
  }
  // Idempotency: if already in the same status, avoid duplicate updates/logs
  if (before && before.status === action) {
    return; // no-op to prevent duplicate updates/logs
  }
  
  // Prevent overwriting terminal statuses with non-refund actions
  // 'expired' should not be changed (it's a system-set final state)
  // 'paid' can be changed to 'removed' (for refunds) but not to 'checkedout' or 'expired'
  if (before && before.status === 'expired') {
    return;
  }
  
  if (before && before.status === 'paid') {
    if (action === 'checkedout' || action === 'expired') {
      console.warn('[updateCartItemAction] Cannot change paid cart item to:', action);
      return;
    }
    // Allow paid → removed for refunds
  }
  
  // All actions map directly to their status
  const dbStatus = action;
  
  const patch = {
    status: dbStatus,
    lastAction: action,
    lastActionAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  if (action === 'removed') patch.removedAt = serverTimestamp();
  if (action === 'expired') patch.expiredAt = serverTimestamp();
  if (action === 'checkedout') patch.checkedoutAt = serverTimestamp();
  if (action === 'paid') patch.paidAt = serverTimestamp();
  
  try {
    await updateDoc(ref, patch);
  } catch (err) {
    console.error('[updateCartItemAction] ❌ Update failed:', err);
    console.error('[updateCartItemAction] Error code:', err.code, 'Message:', err.message);
    throw err; // Re-throw so caller knows it failed
  }

  // Client-side reservation log writes are disabled.
  // Keep suppressLog/triggers for backwards compatibility with callers.
  if (!suppressLog) {
    // no-op
  }

  // Reservation deletion is handled exclusively via the releaseBoothRemote backend callable.
};

/**
 * DEPRECATED: Use updateCartItemAction(cartItemId, 'expired') instead.
 * Write a log indicating this cart item expired due to timeout.
 * This function is kept for backward compatibility but new code should use
 * updateCartItemAction with action='expired' which handles both DB update and logging.
 */
export const logCartExpired = async (cartItemId) => {
  if (!db) throw new Error('Database not initialized');
  if (!cartItemId) throw new Error('Invalid parameters for logCartExpired');
  const ref = doc(db, COLLECTION, cartItemId);
  // Fetch current snapshot for context
  const snap = await getDoc(ref);
  if (!snap.exists()) return; // nothing to log
  // Client-side log write disabled.
};

/**
 * Load all active (status='added') cart items for the given user/session/context.
 * Returns an array of { id, cartItemId, boothId, status, ...data }
 */
export const loadActiveCartItems = async ({ profileId, eventId, layoutId, sessionId }) => {
  if (!db) throw new Error('Database not initialized');
  if (!profileId || !eventId || !layoutId || !sessionId) return [];
  const ref = collection(db, COLLECTION);
  const q = query(
    ref,
    where('profileId', '==', profileId),
    where('eventId', '==', eventId),
    where('layoutId', '==', layoutId),
    where('sessionId', '==', sessionId),
    where('status', '==', 'added')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Load cart items by specific status (e.g., 'checkedout' to mark as paid after payment success)
 * Returns an array of { id, cartItemId, boothId, status, ...data }
 */
export const loadCartItemsByStatus = async ({ profileId, eventId, layoutId, sessionId, status }) => {
  if (!db) throw new Error('Database not initialized');
  if (!profileId || !eventId || !layoutId || !status) return [];
  const ref = collection(db, COLLECTION);
  
  // Build query constraints
  const constraints = [
    where('profileId', '==', profileId),
    where('eventId', '==', eventId),
    where('layoutId', '==', layoutId),
    where('status', '==', status)
  ];
  
  // Only filter by sessionId if provided
  if (sessionId) {
    constraints.push(where('sessionId', '==', sessionId));
  }
  
  const q = query(ref, ...constraints);
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Admin maintenance: Clear old cart items for a given event (across all layouts).
 * Removes items with status 'removed', 'expired' (any age), and 'checkedout' (older than 30 minutes).
 * Returns the number of items cleared.
 */
export const purgeRemovedCartItemsForEvent = async ({ eventId, layoutId = null }) => {
  if (!db) throw new Error('Database not initialized');
  if (!eventId) return 0;
  const ref = collection(db, COLLECTION);
  
  // Calculate 30 minutes ago timestamp
  const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
  
  let allDocsToDelete = [];
  
  // Query 1: Get all 'removed' items
  const removedConstraints = [where('eventId', '==', eventId), where('status', '==', 'removed')];
  if (layoutId) removedConstraints.push(where('layoutId', '==', layoutId));
  const removedQuery = query(ref, ...removedConstraints);
  const removedSnap = await getDocs(removedQuery);
  allDocsToDelete.push(...removedSnap.docs);
  
  // Query 2: Get all 'expired' items
  const expiredConstraints = [where('eventId', '==', eventId), where('status', '==', 'expired')];
  if (layoutId) expiredConstraints.push(where('layoutId', '==', layoutId));
  const expiredQuery = query(ref, ...expiredConstraints);
  const expiredSnap = await getDocs(expiredQuery);
  allDocsToDelete.push(...expiredSnap.docs);
  
  // Query 3: Get all 'checkedout' items older than 30 minutes
  const checkedoutConstraints = [where('eventId', '==', eventId), where('status', '==', 'checkedout')];
  if (layoutId) checkedoutConstraints.push(where('layoutId', '==', layoutId));
  const checkedoutQuery = query(ref, ...checkedoutConstraints);
  const checkedoutSnap = await getDocs(checkedoutQuery);
  
  // Filter checkedout items by age (older than 30 minutes)
  checkedoutSnap.docs.forEach(doc => {
    const data = doc.data();
    const checkedoutAt = data.checkedoutAt;
    if (checkedoutAt) {
      try {
        const checkedoutDate = checkedoutAt.toDate ? checkedoutAt.toDate() : new Date(checkedoutAt);
        if (checkedoutDate < thirtyMinutesAgo) {
          allDocsToDelete.push(doc);
        }
      } catch (err) {
        console.error('[purgeRemovedCartItemsForEvent] Error parsing checkedoutAt:', err);
      }
    }
  });
  
  if (allDocsToDelete.length === 0) return 0;
  
  let count = 0;
  // Chunk deletes to respect batch limit (500)
  const chunkSize = 450;
  for (let i = 0; i < allDocsToDelete.length; i += chunkSize) {
    const batch = writeBatch(db);
    const slice = allDocsToDelete.slice(i, i + chunkSize);
    slice.forEach(d => batch.delete(d.ref));
    await batch.commit();
    count += slice.length;
  }
  return count;
};

/**
 * Count cart items eligible for purging: 'removed', 'expired', and 'checkedout' (older than 30 minutes).
 */
export const countRemovedCartItemsForEvent = async ({ eventId, layoutId = null }) => {
  if (!db) throw new Error('Database not initialized');
  if (!eventId) return 0;
  const ref = collection(db, COLLECTION);
  
  // Calculate 30 minutes ago timestamp
  const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
  
  let totalCount = 0;
  
  // Count 'removed' items
  const removedConstraints = [where('eventId', '==', eventId), where('status', '==', 'removed')];
  if (layoutId) removedConstraints.push(where('layoutId', '==', layoutId));
  const removedQuery = query(ref, ...removedConstraints);
  const removedSnap = await getDocs(removedQuery);
  totalCount += removedSnap.size;
  
  // Count 'expired' items
  const expiredConstraints = [where('eventId', '==', eventId), where('status', '==', 'expired')];
  if (layoutId) expiredConstraints.push(where('layoutId', '==', layoutId));
  const expiredQuery = query(ref, ...expiredConstraints);
  const expiredSnap = await getDocs(expiredQuery);
  totalCount += expiredSnap.size;
  
  // Count 'checkedout' items older than 30 minutes
  const checkedoutConstraints = [where('eventId', '==', eventId), where('status', '==', 'checkedout')];
  if (layoutId) checkedoutConstraints.push(where('layoutId', '==', layoutId));
  const checkedoutQuery = query(ref, ...checkedoutConstraints);
  const checkedoutSnap = await getDocs(checkedoutQuery);
  
  // Filter by age
  let oldCheckedoutCount = 0;
  checkedoutSnap.docs.forEach(doc => {
    const data = doc.data();
    const checkedoutAt = data.checkedoutAt;
    if (checkedoutAt) {
      try {
        const checkedoutDate = checkedoutAt.toDate ? checkedoutAt.toDate() : new Date(checkedoutAt);
        if (checkedoutDate < thirtyMinutesAgo) {
          oldCheckedoutCount++;
        }
      } catch (err) {
        console.error('[countRemovedCartItemsForEvent] Error parsing checkedoutAt:', err);
      }
    }
  });
  totalCount += oldCheckedoutCount;
  
  return totalCount;
};
